import { useCallback, useEffect, useRef, useState } from 'react';

import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type { ClarifyingQuestion, QuestionAnswer } from '@revelio/core';

import { getTextAssist } from '../../api/build-a-dash-ai-gen-api';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { DEFAULT_GRID_COLUMNS } from '../PageConfigProvider/constants';
import type {
  TabContent,
  TabContentAction,
} from '../PageConfigProvider/page-reducer';
import {
  applyConfigToLayout,
  convertReportTabContentToLLM,
  type ReportIntent,
  type ReportLLMPositionedItem,
  type ReportLLMTabContent,
} from './report-layout-helpers';
import { type ReportSSEEvent, readSSEStream } from './report-stream-helpers';
import { trackAIAssistantPrompt } from './track-ai-assistant-prompt';

export type ReportAIEntityType = 'company' | 'geography' | 'role';

export type { ReportIntent, ReportLLMTabContent };

interface ConversationListItem {
  id: string;
  title: string;
  updatedAt: string;
}

interface UseReportAIAssistantOptions {
  primaryParameterType: ReportAIEntityType;
  getCurrentTabContent: () => TabContent;
  dispatch: (action: TabContentAction) => void;
  /**
   * Called once per conversation when the LLM first emits a title in its
   * `intent` event. Lets the caller propagate the AI-generated title to the
   * page name so freshly-created reports aren't left as "Untitled X".
   */
  onTitleGenerated?: (title: string) => void;
}

function formatClarificationAnswers(answers: QuestionAnswer[]): string {
  return answers
    .map((answer) => {
      return `${answer.questionId}:${(answer.selectedOptionIds ?? []).join(',')}`;
    })
    .join(';');
}

const PRIMARY_FILTER_MAP: Record<ReportAIEntityType, PrimaryFilterId> = {
  company: FilterId.COMPANY,
  geography: FilterId.GEOGRAPHY,
  role: FilterId.ROLE,
};

function fireTextAssistRequests(
  llmItems: ReportLLMPositionedItem[],
  textAssistContext: ReportLLMTabContent['textAssistContext'],
  llmIndexToPositionedId: Map<number, string>,
  dispatch: (action: TabContentAction) => void,
  signal: AbortSignal,
  onTextHtmlUpdate?: (positionedId: string, textHtml: string) => void
): void {
  llmItems.forEach((llmItem, i) => {
    const plot = llmItem.plot;
    if (!plot || plot.chartType !== 'text') return;
    const textPrompt =
      typeof plot.textPrompt === 'string' ? plot.textPrompt.trim() : '';
    if (!textPrompt) return;
    const html = plot.textHtml;
    const hasContent =
      html != null && typeof html === 'string' && html.trim().length > 0;
    if (hasContent) return;
    const positionedId = llmIndexToPositionedId.get(i);
    if (!positionedId) return;
    getTextAssist(
      {
        prompt: textPrompt,
        template: '',
        contentSize: plot.contentSize,
        metrics: (textAssistContext?.metrics ?? []).map((m) => ({
          value: m.value,
          description: m.description ?? '',
        })),
        filters: (textAssistContext?.filters ?? []).map((f) => ({
          id: f.id,
          label: f.label ?? '',
        })),
      },
      { signal }
    )
      .then((result) => {
        dispatch({
          type: 'UPDATE_ITEM_TEXT_HTML',
          payload: {
            id: positionedId,
            textHtml: result.template,
          },
        });
        onTextHtmlUpdate?.(positionedId, result.template);
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        const fallback = '<p>Unable to load content. Please try again.</p>';
        dispatch({
          type: 'UPDATE_ITEM_TEXT_HTML',
          payload: { id: positionedId, textHtml: fallback },
        });
        onTextHtmlUpdate?.(positionedId, fallback);
      });
  });
}

export interface UseReportAIAssistantResult {
  thinking: string[];
  status: string | null;
  intent: ReportIntent | null;
  isGenerating: boolean;
  isFixing: boolean;
  isLoadingConversation: boolean;
  error: string | null;
  promptHistory: string[];
  configSnapshots: ReportLLMTabContent[];
  responseSummaries: string[];
  conversationTitle: string | null;
  questions: ClarifyingQuestion[] | null;
  conversations: ConversationListItem[];
  conversationId: string | null;
  continueWithAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  skipQuestions: () => Promise<void>;
  generate: (prompt: string, model?: string) => Promise<void>;
  update: (newPrompt: string, model?: string) => Promise<void>;
  fixCharts: (
    _originalPrompt: string,
    _chartErrors: Array<{
      chartId: string;
      chartTitle?: string;
      chartType: string;
      metric: string;
      secondaryDimension?: string;
      reason: string;
    }>,
    _model?: string
  ) => Promise<void>;
  restoreConfig: (index: number) => void;
  reset: () => void;
  saveConversation: () => Promise<void>;
  loadConversation: (_id: string) => Promise<void>;
  fetchConversations: () => Promise<void>;
}

export function useReportAIAssistant({
  primaryParameterType,
  getCurrentTabContent,
  dispatch,
  onTitleGenerated,
}: UseReportAIAssistantOptions): UseReportAIAssistantResult {
  const onTitleGeneratedRef = useRef(onTitleGenerated);
  useEffect(() => {
    onTitleGeneratedRef.current = onTitleGenerated;
  }, [onTitleGenerated]);
  const [thinking, setThinking] = useState<string[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [intent, setIntent] = useState<ReportIntent | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [configSnapshots, setConfigSnapshots] = useState<ReportLLMTabContent[]>(
    []
  );
  const [responseSummaries, setResponseSummaries] = useState<string[]>([]);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null
  );
  const [questions, setQuestions] = useState<ClarifyingQuestion[] | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    []
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const latestIntentRef = useRef<ReportIntent | null>(null);
  const isLoadingConversationRef = useRef(false);
  const positionedIdToLlmIndexRef = useRef<Map<string, number>>(new Map());
  const conversationTitleRef = useRef<string | null>(null);
  const promptHistoryRef = useRef<string[]>([]);
  const trackingConversationIdRef = useRef(crypto.randomUUID());
  const getTrackingConversationId = useCallback(() => {
    return trackingConversationIdRef.current;
  }, []);

  useEffect(() => {
    promptHistoryRef.current = promptHistory;
  }, [promptHistory]);

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setThinking([]);
    setStatus(null);
    setIntent(null);
    setIsGenerating(false);
    setIsFixing(false);
    setError(null);
    setPromptHistory([]);
    setConfigSnapshots([]);
    setResponseSummaries([]);
    setConversationTitle(null);
    conversationTitleRef.current = null;
    latestIntentRef.current = null;
    setQuestions(null);
    setPendingPrompt(null);
    setConversationId(null);
    trackingConversationIdRef.current = crypto.randomUUID();
  }, []);

  const generate = useCallback(
    async (
      prompt: string,
      model?: string,
      options?: {
        answers?: QuestionAnswer[];
        skipAddToHistory?: boolean;
        promptMode?: 'generate' | 'clarification_answer';
      }
    ) => {
      const promptMode = options?.promptMode ?? 'generate';
      if (!options?.skipAddToHistory) {
        trackAIAssistantPrompt({
          assistantSurface: 'report',
          conversationId: getTrackingConversationId(),
          turnIndex: promptHistoryRef.current.length + 1,
          promptText: prompt,
          promptMode,
          entityType: primaryParameterType,
        });
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsGenerating(true);
      setThinking([]);
      setStatus(null);
      setIntent(null);
      setError(null);
      setQuestions(null);
      setPendingPrompt(null);
      if (!options?.skipAddToHistory) {
        setPromptHistory((prev) => [...prev, prompt]);
      }

      const csrfToken = getAuthDashMetaCsrfToken();
      const answers = options?.answers;

      try {
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/report-stream`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              prompt,
              primaryParameterType,
              ...(model && { model }),
              ...(answers && answers.length > 0 && { answers }),
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const primaryFilterId = PRIMARY_FILTER_MAP[primaryParameterType];

        const handleEvent = (event: ReportSSEEvent) => {
          switch (event.type) {
            case 'thinking':
              setThinking((prev) => [...prev, event.content]);
              break;
            case 'status':
              setStatus(event.content);
              break;
            case 'intent':
              setIntent(event.content);
              latestIntentRef.current = event.content;
              if (event.content.title) {
                const isFirstTitle = !conversationTitleRef.current;
                conversationTitleRef.current = event.content.title;
                setConversationTitle(event.content.title);
                dispatch({
                  type: 'UPDATE_REPORT_TITLE',
                  payload: { title: event.content.title },
                });
                if (isFirstTitle) {
                  onTitleGeneratedRef.current?.(event.content.title);
                }
              }
              if (event.content.summary) {
                dispatch({
                  type: 'UPDATE_REPORT_DESCRIPTION',
                  payload: { description: event.content.summary },
                });
              }
              break;
            case 'config': {
              const {
                items: itemsToSet,
                gridRows,
                llmIndexToPositionedId,
              } = applyConfigToLayout(event.content.items, primaryFilterId);
              positionedIdToLlmIndexRef.current = new Map(
                [...llmIndexToPositionedId].map(([k, v]) => [v, k])
              );
              dispatch({
                type: 'SET_ITEMS',
                payload: itemsToSet,
              });
              dispatch({
                type: 'SET_GRID',
                payload: { rows: gridRows, columns: DEFAULT_GRID_COLUMNS },
              });
              setConfigSnapshots((prev) => [...prev, event.content]);
              if (options?.skipAddToHistory) {
                setPromptHistory((prev) => [...prev, prompt]);
              }
              const currentIntent = latestIntentRef.current;
              setResponseSummaries((prev) => [
                ...prev,
                currentIntent?.summary ?? '',
              ]);

              fireTextAssistRequests(
                event.content.items,
                event.content.textAssistContext,
                llmIndexToPositionedId,
                dispatch,
                abortController.signal,
                (positionedId, textHtml) => {
                  setConfigSnapshots((prev) => {
                    if (prev.length === 0) return prev;
                    const idx =
                      positionedIdToLlmIndexRef.current.get(positionedId);
                    if (idx === undefined) return prev;
                    const last = prev[prev.length - 1];
                    if (!last) return prev;
                    const item = last.items[idx];
                    if (!item?.plot || item.plot.chartType !== 'text') {
                      return prev;
                    }
                    const nextLast: ReportLLMTabContent = {
                      ...last,
                      items: last.items.map((it, i) =>
                        i === idx && it.plot?.chartType === 'text'
                          ? {
                              ...it,
                              plot: { ...it.plot, textHtml },
                            }
                          : it
                      ),
                    };
                    return [...prev.slice(0, -1), nextLast];
                  });
                }
              );
              break;
            }
            case 'questions':
              setQuestions(event.content.questions);
              setPendingPrompt(prompt);
              setIsGenerating(false);
              abortController.abort();
              break;
            case 'error':
              setError(event.content);
              break;
          }
        };

        await readSSEStream(reader, handleEvent);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        if (!options?.skipAddToHistory) {
          setPromptHistory((prev) => prev.slice(0, -1));
        }
      } finally {
        setIsGenerating(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [primaryParameterType, dispatch, getTrackingConversationId]
  );

  const continueWithAnswers = useCallback(
    async (answers: QuestionAnswer[]) => {
      if (!pendingPrompt) return;
      setQuestions(null);
      const promptToUse = pendingPrompt;
      const promptText = formatClarificationAnswers(answers);
      trackAIAssistantPrompt({
        assistantSurface: 'report',
        conversationId: getTrackingConversationId(),
        turnIndex: promptHistoryRef.current.length + 1,
        promptText,
        promptMode: 'clarification_answer',
        entityType: primaryParameterType,
      });
      setPendingPrompt(null);
      await generate(promptToUse, undefined, {
        answers,
        skipAddToHistory: true,
        promptMode: 'clarification_answer',
      });
    },
    [pendingPrompt, generate, getTrackingConversationId, primaryParameterType]
  );

  const skipQuestions = useCallback(async () => {
    setQuestions(null);
  }, []);

  const update = useCallback(
    async (newPrompt: string, model?: string) => {
      const currentTabContent = getCurrentTabContent();
      const currentConfig = convertReportTabContentToLLM(currentTabContent);
      if (currentConfig.items.length === 0) {
        await generate(newPrompt, model);
        return;
      }

      trackAIAssistantPrompt({
        assistantSurface: 'report',
        conversationId: getTrackingConversationId(),
        turnIndex: promptHistoryRef.current.length + 1,
        promptText: newPrompt,
        promptMode: 'update',
        entityType: primaryParameterType,
      });

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsGenerating(true);
      setThinking([]);
      setStatus(null);
      setError(null);
      setQuestions(null);
      setPendingPrompt(null);
      setPromptHistory((prev) => [...prev, newPrompt]);

      const csrfToken = getAuthDashMetaCsrfToken();

      try {
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/report-update-from-config-stream`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              newPrompt,
              currentConfig,
              previousPrompts: promptHistory.slice(-3),
              primaryParameterType,
              ...(model && { model }),
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const primaryFilterId = PRIMARY_FILTER_MAP[primaryParameterType];

        const handleEvent = (event: ReportSSEEvent) => {
          switch (event.type) {
            case 'thinking':
              setThinking((prev) => [...prev, event.content]);
              break;
            case 'status':
              setStatus(event.content);
              break;
            case 'intent':
              setIntent(event.content);
              latestIntentRef.current = event.content;
              if (event.content.title) {
                const isFirstTitle = !conversationTitleRef.current;
                conversationTitleRef.current = event.content.title;
                setConversationTitle(event.content.title);
                dispatch({
                  type: 'UPDATE_REPORT_TITLE',
                  payload: { title: event.content.title },
                });
                if (isFirstTitle) {
                  onTitleGeneratedRef.current?.(event.content.title);
                }
              }
              if (event.content.summary) {
                dispatch({
                  type: 'UPDATE_REPORT_DESCRIPTION',
                  payload: { description: event.content.summary },
                });
              }
              break;
            case 'config': {
              const {
                items: itemsToSet,
                gridRows,
                llmIndexToPositionedId,
              } = applyConfigToLayout(event.content.items, primaryFilterId);
              positionedIdToLlmIndexRef.current = new Map(
                [...llmIndexToPositionedId].map(([k, v]) => [v, k])
              );
              dispatch({
                type: 'SET_ITEMS',
                payload: itemsToSet,
              });
              dispatch({
                type: 'SET_GRID',
                payload: { rows: gridRows, columns: DEFAULT_GRID_COLUMNS },
              });
              setConfigSnapshots((prev) => [...prev, event.content]);
              const currentIntent = latestIntentRef.current;
              setResponseSummaries((prev) => [
                ...prev,
                currentIntent?.summary ?? '',
              ]);

              fireTextAssistRequests(
                event.content.items,
                event.content.textAssistContext,
                llmIndexToPositionedId,
                dispatch,
                abortController.signal,
                (positionedId, textHtml) => {
                  setConfigSnapshots((prev) => {
                    if (prev.length === 0) return prev;
                    const idx =
                      positionedIdToLlmIndexRef.current.get(positionedId);
                    if (idx === undefined) return prev;
                    const last = prev[prev.length - 1];
                    if (!last) return prev;
                    const item = last.items[idx];
                    if (!item?.plot || item.plot.chartType !== 'text') {
                      return prev;
                    }
                    const nextLast: ReportLLMTabContent = {
                      ...last,
                      items: last.items.map((it, i) =>
                        i === idx && it.plot?.chartType === 'text'
                          ? {
                              ...it,
                              plot: { ...it.plot, textHtml },
                            }
                          : it
                      ),
                    };
                    return [...prev.slice(0, -1), nextLast];
                  });
                }
              );
              break;
            }
            case 'questions':
              setQuestions(event.content.questions);
              setPendingPrompt(newPrompt);
              setIsGenerating(false);
              abortController.abort();
              break;
            case 'error':
              setError(event.content);
              break;
          }
        };

        await readSSEStream(reader, handleEvent);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setPromptHistory((prev) => prev.slice(0, -1));
      } finally {
        setIsGenerating(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      primaryParameterType,
      getCurrentTabContent,
      promptHistory,
      dispatch,
      generate,
      getTrackingConversationId,
    ]
  );

  const fixCharts = useCallback(
    async (
      _originalPrompt: string,
      _chartErrors: Array<{
        chartId: string;
        chartTitle?: string;
        chartType: string;
        metric: string;
        secondaryDimension?: string;
        reason: string;
      }>,
      _model?: string
    ) => {
      setError('Report fix charts is not yet supported.');
    },
    []
  );

  const restoreConfig = useCallback(
    (index: number) => {
      const snapshot = configSnapshots[index];
      if (!snapshot) return;
      const primaryFilterId = PRIMARY_FILTER_MAP[primaryParameterType];
      const {
        items: itemsToSet,
        gridRows,
        llmIndexToPositionedId,
      } = applyConfigToLayout(snapshot.items, primaryFilterId);
      dispatch({
        type: 'SET_ITEMS',
        payload: itemsToSet,
      });
      dispatch({
        type: 'SET_GRID',
        payload: { rows: gridRows, columns: DEFAULT_GRID_COLUMNS },
      });
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const ac = new AbortController();
      abortControllerRef.current = ac;
      fireTextAssistRequests(
        snapshot.items,
        snapshot.textAssistContext,
        llmIndexToPositionedId,
        dispatch,
        ac.signal
      );
    },
    [configSnapshots, primaryParameterType, dispatch]
  );

  const fetchConversations = useCallback(async () => {
    const csrfToken = getAuthDashMetaCsrfToken();

    try {
      const response = await fetch(
        `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations?entityType=${primaryParameterType}&pageType=report`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken ?? '',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }

      const data = await response.json();
      setConversations(data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  }, [primaryParameterType]);

  const saveConversation = useCallback(async () => {
    if (
      promptHistory.length === 0 ||
      configSnapshots.length === 0 ||
      !latestIntentRef.current
    ) {
      return;
    }

    const intent = latestIntentRef.current;
    const csrfToken = getAuthDashMetaCsrfToken();
    const title =
      conversationTitle ||
      intent.title ||
      intent.summary?.substring(0, 50) ||
      promptHistory[0]?.substring(0, 50) ||
      'Untitled Report';

    try {
      if (conversationId) {
        const res = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations/${conversationId}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              promptHistory,
              configSnapshots,
              latestIntent: intent,
              responseSummaries,
            }),
          }
        );
        if (res.ok) {
          await fetchConversations();
        }
      } else {
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              entityType: primaryParameterType,
              pageType: 'report',
              title,
              promptHistory,
              configSnapshots,
              latestIntent: intent,
              responseSummaries,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setConversationId(data.id);
          await fetchConversations();
        }
      }
    } catch (err) {
      console.error('Failed to save conversation:', err);
    }
  }, [
    conversationId,
    primaryParameterType,
    promptHistory,
    configSnapshots,
    responseSummaries,
    conversationTitle,
    fetchConversations,
  ]);

  const loadConversation = useCallback(async (id: string) => {
    const csrfToken = getAuthDashMetaCsrfToken();

    isLoadingConversationRef.current = true;
    setIsLoadingConversation(true);

    try {
      const response = await fetch(
        `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations/${id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken ?? '',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load conversation: ${response.status}`);
      }

      const conversation = await response.json();

      setConversationId(conversation.id);
      setPromptHistory(conversation.promptHistory ?? []);
      setConfigSnapshots(conversation.configSnapshots ?? []);
      setResponseSummaries(conversation.responseSummaries ?? []);
      setConversationTitle(conversation.title ?? null);
      conversationTitleRef.current = conversation.title ?? null;
      setIntent(conversation.latestIntent ?? null);
      latestIntentRef.current = conversation.latestIntent ?? null;
      setQuestions(null);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load conversation'
      );
    } finally {
      setIsLoadingConversation(false);
      isLoadingConversationRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (
      configSnapshots.length > 0 &&
      promptHistory.length > 0 &&
      latestIntentRef.current &&
      !isGenerating &&
      !isLoadingConversationRef.current
    ) {
      const timeoutId = setTimeout(() => {
        saveConversation();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [
    configSnapshots.length,
    promptHistory.length,
    isGenerating,
    saveConversation,
  ]);

  return {
    thinking,
    status,
    intent,
    isGenerating,
    isFixing,
    isLoadingConversation,
    error,
    promptHistory,
    configSnapshots,
    responseSummaries,
    conversationTitle,
    questions,
    conversations,
    conversationId,
    continueWithAnswers,
    skipQuestions,
    generate,
    update,
    fixCharts,
    restoreConfig,
    reset,
    saveConversation,
    loadConversation,
    fetchConversations,
  };
}
